import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Truck, CheckCircle2, PauseCircle, XCircle, Printer, Download } from "lucide-react";
import CustomerDrawer from "@/components/admin/CustomerDrawer";

const STATUS_OPTIONS = ["processing", "shipped", "delivered", "evaso", "sospeso", "annullato"];
const STATUS_STYLES = {
  processing: "bg-slate-100 text-slate-700",
  shipped: "bg-blue-50 text-blue-700",
  delivered: "bg-emerald-50 text-emerald-700",
  evaso: "bg-emerald-100 text-emerald-800",
  sospeso: "bg-amber-50 text-amber-800",
  annullato: "bg-red-50 text-red-700",
  cancelled: "bg-red-50 text-red-700",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [drawerCustomer, setDrawerCustomer] = useState(null); // {customerId, email}
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = () => api.get("/admin/orders", { params: { search: q || undefined } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [q]);

  const update = async (id, status, tracking) => {
    await api.put(`/admin/orders/${id}`, { status, tracking_number: tracking });
    toast.success("Ordine aggiornato");
    load();
    setDetail(null);
  };

  const doAction = async (id, action) => {
    const labels = { evaso: "Evadere", sospeso: "Sospendere", annullato: "Annullare" };
    if (!window.confirm(`${labels[action]} l'ordine? ${action === "evaso" ? "Fattura e bolla verranno generate automaticamente." : ""}`)) return;
    try {
      await api.post(`/admin/orders/${id}/action`, { action });
      toast.success(`Ordine ${action}`);
      load();
      setDetail(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const openDocument = (orderId, kind) => {
    const token = localStorage.getItem("xd_admin_token");
    // Fetch protected HTML, open in new tab via blob URL
    fetch(`${API}/api/admin/orders/${orderId}/${kind}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const w = window.open("", "_blank");
        if (w) { w.document.open(); w.document.write(html); w.document.close(); }
      })
      .catch(() => toast.error("Errore apertura documento"));
  };

  // CSV export — reuses the same bearer-token+blob pattern as the invoice download
  // so Chrome/Safari trigger a native file save prompt. Respects the current search
  // filter: typing "AUD-26" and clicking Export yields a filtered CSV.
  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("xd_admin_token");
      const url = `${API}/api/admin/export/orders.csv${q ? `?search=${encodeURIComponent(q)}` : ""}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("http " + r.status);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audrivio-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("CSV scaricato");
    } catch { toast.error("Errore export CSV"); }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Vendite</div>
          <h1 className="font-admin text-2xl font-bold">Ordini</h1>
        </div>
        <div className="flex items-center gap-3">
          <Input placeholder="Cerca per # o email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="orders-search" className="w-64" />
          <button
            type="button"
            onClick={exportCsv}
            data-testid="orders-export-csv"
            className="inline-flex items-center gap-2 bg-xd-dark hover:bg-xd-dark2 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" /> Esporta CSV
          </button>
        </div>
      </div>

      <div className="bg-white border border-xd-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-xd-light border-b border-xd-border">
            <tr>
              {["Numero", "Cliente", "Totale", "Pagamento", "Stato", "Data", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-xd-muted">Nessun ordine ancora</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-xd-border last:border-b-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono-tech text-xs">
                  <div>{o.order_number}</div>
                  {o.invoice_number && <div className="text-[10px] text-xd-muted">Fatt. {o.invoice_number}</div>}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setDrawerCustomer({ customerId: o.customer_id, email: o.customer_email })}
                    data-testid={`order-customer-${o.id}`}
                    className="text-xd-cyanHover hover:underline"
                    title="Vedi scheda cliente"
                  >
                    {o.customer_email}
                  </button>
                </td>
                <td className="px-4 py-3 font-mono-tech">€ {o.total.toFixed(2)}</td>
                <td className="px-4 py-3 text-xs">{o.payment_method} · {o.payment_status}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLES[o.status] || "bg-slate-100"}`}>{o.status}</span></td>
                <td className="px-4 py-3 text-xs text-xd-muted">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
                <td className="px-4 py-3 text-right"><button type="button" onClick={() => setDetail(o)} data-testid={`order-view-${o.id}`} className="text-xd-cyanHover text-xs font-semibold hover:underline">Dettagli</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-admin">Ordine {detail.order_number}</DialogTitle></DialogHeader>
              <div className="grid gap-4">
                <div className="text-xs text-xd-muted">Cliente: <strong className="text-xd-text">{detail.shipping?.full_name}</strong> · {detail.customer_email}</div>
                <div className="text-xs text-xd-muted">Spedizione: {detail.shipping?.address_line}, {detail.shipping?.city} {detail.shipping?.postal_code}, {detail.shipping?.country}</div>
                {detail.supplier_discount_percent > 0 && (
                  <div className="text-xs bg-xd-cyan/10 text-xd-cyanHover px-3 py-1.5 rounded inline-block">Sconto fornitore: -{detail.supplier_discount_percent}%</div>
                )}
                <div className="rounded-lg border divide-y">
                  {detail.items?.map((i, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3">
                      <div className="w-10 h-10 rounded bg-slate-100 overflow-hidden">{i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}</div>
                      <div className="flex-1 text-sm">
                        <div className="font-medium">{i.name}</div>
                        <div className="text-xs text-xd-muted">Qtà: {i.quantity} · € {i.price.toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Documents */}
                <div className="border border-xd-border rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] font-mono-tech uppercase tracking-widest text-xd-muted mb-2">Documenti</div>
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={() => openDocument(detail.id, "invoice")} data-testid="order-open-invoice" className="inline-flex items-center gap-1 h-9 px-3 rounded bg-white border border-xd-border text-xs hover:border-xd-cyan">
                      <FileText className="w-3.5 h-3.5" /> {detail.invoice_number ? `Fattura ${detail.invoice_number}` : "Genera fattura"}
                    </button>
                    <button type="button" onClick={() => openDocument(detail.id, "delivery-note")} data-testid="order-open-ddt" className="inline-flex items-center gap-1 h-9 px-3 rounded bg-white border border-xd-border text-xs hover:border-xd-cyan">
                      <Truck className="w-3.5 h-3.5" /> {detail.delivery_note_number ? `DDT ${detail.delivery_note_number}` : "Genera bolla"}
                    </button>
                    <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1 h-9 px-3 rounded bg-white border border-xd-border text-xs hover:border-xd-cyan" title="Stampa questa scheda">
                      <Printer className="w-3.5 h-3.5" /> Stampa
                    </button>
                  </div>
                </div>

                {/* Quick actions */}
                <div className="border border-xd-border rounded-lg p-3">
                  <div className="text-[11px] font-mono-tech uppercase tracking-widest text-xd-muted mb-2">Azioni ordine</div>
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={() => doAction(detail.id, "evaso")} data-testid="order-action-evaso" className="inline-flex items-center gap-1 h-9 px-3 rounded bg-emerald-600 text-white text-xs hover:opacity-90">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Evadi
                    </button>
                    <button type="button" onClick={() => doAction(detail.id, "sospeso")} data-testid="order-action-sospeso" className="inline-flex items-center gap-1 h-9 px-3 rounded bg-amber-500 text-white text-xs hover:opacity-90">
                      <PauseCircle className="w-3.5 h-3.5" /> Sospendi
                    </button>
                    <button type="button" onClick={() => doAction(detail.id, "annullato")} data-testid="order-action-annullato" className="inline-flex items-center gap-1 h-9 px-3 rounded bg-red-600 text-white text-xs hover:opacity-90">
                      <XCircle className="w-3.5 h-3.5" /> Annulla
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <select defaultValue={detail.status} id="status-sel" className="border border-xd-border rounded-md px-3 py-2 text-sm" data-testid="order-status-select">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <Input defaultValue={detail.tracking_number || ""} placeholder="Numero tracking" id="tracking-sel" data-testid="order-tracking-input" />
                </div>
                <button type="button" onClick={() => {
                  const s = document.getElementById("status-sel").value;
                  const t = document.getElementById("tracking-sel").value;
                  update(detail.id, s, t);
                }} data-testid="order-save" className="btn-cta rounded-lg px-4 py-2 text-sm">Aggiorna ordine</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CustomerDrawer
        open={!!drawerCustomer}
        onOpenChange={(o) => !o && setDrawerCustomer(null)}
        customerId={drawerCustomer?.customerId}
        email={drawerCustomer?.email}
      />
    </AdminLayout>
  );
}
